import React, { useEffect, useMemo, useState } from "react";
import { IoEyeOutline } from "react-icons/io5";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Select from "react-select";

const ITEMS_PER_PAGE = 8;
const PAGE_GROUP_SIZE = 5;

export default function RegisteredMembers() {
  const navigate = useNavigate();
  const API_URL = process.env.REACT_APP_FRONTEND_URL;

  // Data + status
  const [employeeData, setEmployeeData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [fetchError, setFetchError] = useState("");
  const [designationOptions, setDesignationOptions] = useState([]);
  const [departmentOptions, setDepartmentOptions] = useState([]);
  const [organizationOptions, setOrganizationOptions] = useState([]);

  const [filters, setFilters] = useState({
    search: "",
    employee_active_status: "",
    employee_nic: "",
    departmentId: "",
    designationId: "",
    epf_no: "",
    organization_id: "", // Changed to organization_id for consistency
  });

  // Effect to reset department/designation filters when organization changes
  useEffect(() => {
    // This effect runs when filters.organization_id changes.
    // It clears department and designation selections and their options
    // to ensure consistency with the newly selected organization.
    setFilters((prevFilters) => ({
      ...prevFilters,
      departmentId: "", // Reset department selection
      designationId: "", // Reset designation selection
    }));
    setDepartmentOptions([]); // Clear department options
    setDesignationOptions([]); // Clear designation options
  }, [filters.organization_id]); // Only run when organization_id changes


  // Fetch Designations and Departments (Modified to filter by organization_id)
  useEffect(() => {
    const fetchDesignationsAndDepartments = async () => {
      // Only fetch if an organization is selected
      if (!filters.organization_id) {
        setDesignationOptions([]); // Ensure options are cleared if no organization selected
        setDepartmentOptions([]); // Ensure options are cleared if no organization selected
        return;
      }

      try {
        const token = document.cookie
          .split("; ")
          .find((row) => row.startsWith("user_token="))
          ?.split("=")[1];

        const params = new URLSearchParams();
        params.set("organization_id", filters.organization_id);

        // Fetch designations from the organization-specific endpoint
        const res = await fetch(`${API_URL}/v1/hris/organizations/designations?${params.toString()}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          credentials: "include",
        });

        const json = await res.json();
        if (json.success && Array.isArray(json.data)) {
          // Populate designation options
          setDesignationOptions(
            json.data.map((d) => ({
              value: d.id,
              label: `${d.title} (${d?.Department?.name || "No Dept"})`,
            }))
          );

          // Populate department options from the same response
          const uniqueDepartments = new Map();
          json.data.forEach((d) => {
            if (d.Department && d.Department.id && d.Department.name) {
              uniqueDepartments.set(d.Department.id, {
                value: d.Department.id,
                label: d.Department.name,
              });
            }
          });
          setDepartmentOptions(Array.from(uniqueDepartments.values()));

        } else {
            setDesignationOptions([]);
            setDepartmentOptions([]);
        }
      } catch (err) {
        console.error("Failed to fetch designations and departments", err);
        setDesignationOptions([]);
        setDepartmentOptions([]);
      }
    };

    fetchDesignationsAndDepartments();
  }, [API_URL, filters.organization_id]); // Add filters.organization_id to dependencies


  // Fetch Organizations (unchanged)
  useEffect(() => {
    const fetchOrganizations = async () => {
      try {
        const token = document.cookie
          .split("; ")
          .find((row) => row.startsWith("user_token="))
          ?.split("=")[1];

        const res = await fetch(`${API_URL}/v1/hris/organizations/organization`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          credentials: "include",
        });

        const json = await res.json();
        if (json.success && Array.isArray(json.data)) {
          setOrganizationOptions(
            json.data.map((org) => ({
              value: org.id,
              label: org.organization_name,
            }))
          );
        }
      } catch (err) {
        console.error("Failed to fetch organizations", err);
      }
    };

    fetchOrganizations();
  }, [API_URL]);


  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [pageGroupStart, setPageGroupStart] = useState(1);

  const visiblePageNumbers = useMemo(() => {
    const remaining = Math.max(totalPages - pageGroupStart + 1, 0);
    const len = Math.min(PAGE_GROUP_SIZE, remaining);
    return Array.from({ length: len }, (_, i) => pageGroupStart + i);
  }, [pageGroupStart, totalPages]);


  // Fetch Employees
  const fetchEmployees = async () => {
    try {
      setIsLoading(true);
      setFetchError("");

      const token = document.cookie
        .split("; ")
        .find((row) => row.startsWith("accessToken="))
        ?.split("=")[1];

      const params = new URLSearchParams({
        page: String(currentPage),
        limit: String(ITEMS_PER_PAGE),
      });

      if (filters.search.trim())
        params.set("search", filters.search.trim());

      if (filters.employee_active_status)
        params.set("employee_active_status", filters.employee_active_status);

      if (filters.employee_nic.trim())
        params.set("employee_nic", filters.employee_nic.trim());

      if (filters.departmentId)
        params.set("departmentId", filters.departmentId);

      if (filters.designationId)
        params.set("designationId", filters.designationId);

      if (filters.epf_no.trim())
        params.set("epf_no", filters.epf_no.trim());

      if (filters.organization_id) // Using organization_id
        params.set("organization", filters.organization_id);


      const res = await fetch(
        `${API_URL}/v1/hris/employees/employee/all-details?${params.toString()}`,
        {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          credentials: "include",
        }
      );
      const json = await res.json().catch(() => ({}));

      if (!res.ok) throw new Error(json?.message || `HTTP ${res.status}`);

      const data = Array.isArray(json?.data) ? json.data : [];
      setEmployeeData(data);
      setTotalRecords(Number(json?.totalRecords) || data.length || 0);
      setTotalPages(Number(json?.totalPages) || 1);

      const serverPage = Number(json?.page) || currentPage;
      if (serverPage !== currentPage) setCurrentPage(serverPage);
    } catch (err) {
      setEmployeeData([]);
      setTotalRecords(0);
      setTotalPages(1);
      setFetchError("Failed to fetch employees.");
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  // Trigger fetch on filters/page change
  useEffect(() => {
    fetchEmployees();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, currentPage]);

  // Reset pagination group when page changes out of range
  useEffect(() => {
    if (currentPage < pageGroupStart) setPageGroupStart(currentPage);
    if (currentPage >= pageGroupStart + PAGE_GROUP_SIZE)
      setPageGroupStart(currentPage - PAGE_GROUP_SIZE + 1);
  }, [currentPage, pageGroupStart]);

  const avatarBgClass = (seed = "") => {
    const palette = [
      "bg-sky-500", "bg-indigo-500", "bg-emerald-500", "bg-rose-500",
      "bg-amber-500", "bg-teal-500", "bg-fuchsia-500", "bg-cyan-500",
    ];
    let hash = 0;
    for (let i = 0; i < seed.length; i++)
      hash = (hash * 31 + seed.charCodeAt(i)) >>> 0;
    return palette[hash % palette.length];
  };

  const getInitials = (fullName = "") => {
    const tokens = String(fullName)
      .replace(/[^\p{L}\p{N}\s'-]/gu, "")
      .trim()
      .split(/\s+/)
      .filter(Boolean);
    if (tokens.length === 0) return "??";
    if (tokens.length === 1) return tokens[0].slice(0, 2).toUpperCase();
    const first = tokens[0][0] || "";
    const last = tokens[tokens.length - 1][0] || "";
    return (first + last).toUpperCase();
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: 50 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -50 }}
      transition={{ duration: 0.4 }}
    >
      <div className="font-montserrat">
        <p className="mt-5 flex justify-start text-[25px] mb-4">
          Registered Employee List
        </p>

        {/* Filters */}
        <div className="flex flex-wrap gap-4 mb-6">
            {/* Organization Filter */}
          <div className="w-[220px]">
            <Select
              options={organizationOptions}
              placeholder="Select Organization"
              value={organizationOptions.find(
                (opt) => opt.value === Number(filters.organization_id)
              ) || null}
              onChange={(opt) =>
                setFilters((f) => ({ ...f, organization_id: opt ? opt.value : "" }))
              }
              isClearable
            />
          </div>

          <input
            type="text"
            placeholder="Search by Employee No / Name"
            className="border border-gray-300 rounded-md p-2 w-[220px]"
            value={filters.search}
            onChange={(e) =>
              setFilters((f) => ({ ...f, search: e.target.value }))
            }
          />

          <input
            type="text"
            placeholder="EPF No"
            className="border border-gray-300 rounded-md p-2 w-[180px]"
            value={filters.epf_no}
            onChange={(e) =>
              setFilters((f) => ({ ...f, epf_no: e.target.value }))
            }
          />

          <div className="w-[220px]">
            <Select
              options={departmentOptions}
              placeholder="Select Department"
              value={departmentOptions.find(
                (opt) => opt.value === Number(filters.departmentId)
              ) || null}
              onChange={(opt) =>
                setFilters((f) => ({ ...f, departmentId: opt ? opt.value : "" }))
              }
              isClearable
              isDisabled={!filters.organization_id}
            />
          </div>

          <div className="w-[220px]">
            <Select
              options={designationOptions}
              placeholder="Select Designation"
              value={designationOptions.find(
                (opt) => opt.value === Number(filters.designationId)
              ) || null}
              onChange={(opt) =>
                setFilters((f) => ({ ...f, designationId: opt ? opt.value : "" }))
              }
              isClearable
              isDisabled={!filters.organization_id}
            />
          </div>

        

          <input
            type="text"
            placeholder="NIC"
            className="border border-gray-300 rounded-md p-2 w-[180px]"
            value={filters.employee_nic}
            onChange={(e) =>
              setFilters((f) => ({ ...f, employee_nic: e.target.value }))
            }
          />

          <select
            className="border border-gray-300 rounded-md p-2 w-[180px]"
            value={filters.employee_active_status}
            onChange={(e) =>
              setFilters((f) => ({
                ...f,
                employee_active_status: e.target.value,
              }))
            }
          >
            <option value="">Status</option>
            <option value="Active">Active</option>
            <option value="Inactive">Inactive</option>
          </select>

          <button
            className="bg-red-500 text-white px-3 py-1 rounded-md"
            onClick={() => {
              setFilters({
                search: "",
                employee_active_status: "",
                employee_nic: "",
                departmentId: "",
                designationId: "",
                epf_no: "",
                organization_id: "",
              });

              setCurrentPage(1);
              setPageGroupStart(1);
            }}
          >
            Reset
          </button>
        </div>

        {/* Table */}
        <div className="bg-white p-6 rounded-md shadow">
          <table className="w-full text-left text-sm">
            <thead className="text-gray-600 bg-gray-100 uppercase">
              <tr>
                <th className="p-3">Employee No</th>
                <th className="p-3">Full Name</th>
                <th className="p-3">Calling Name</th>
                <th className="p-3">Gender</th>
                <th className="p-3">Email</th>
                <th className="p-3">NIC</th>
                <th className="p-3">Contatct No</th>
                <th className="p-3">Department</th>
                <th className="p-3">Designation</th>
                <th className="p-3">Actions</th>
              </tr>
            </thead>

            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={10} className="text-center py-6">
                    Loading…
                  </td>
                </tr>
              ) : fetchError ? (
                <tr>
                  <td colSpan={10} className="text-center py-6 text-red-600">
                    {fetchError}
                  </td>
                </tr>
              ) : employeeData.length === 0 ? (
                <tr>
                  <td colSpan={10} className="text-center py-6">
                    No data found.
                  </td>
                </tr>
              ) : (
                employeeData.map((emp) => (
                  <tr
                    key={emp.id}
                    className="border-b border-gray-200 hover:bg-gray-50"
                  >
                    <td className="px-3 py-3">{emp.employee_no || "—"}</td>
                    <td className="px-3 py-3 font-medium text-gray-700">
                      <div className="flex items-center gap-2">
                        <div
                          className={`w-8 h-8 flex items-center justify-center rounded-full text-white text-xs font-semibold ${avatarBgClass(
                            emp.employee_fullname || emp.employee_no || ""
                          )}`}
                        >
                          {getInitials(emp.employee_fullname)}
                        </div>
                        <span>{emp.employee_fullname || "—"}</span>
                      </div>
                    </td>

                    <td className="px-3 py-3 font-medium text-gray-700">
                      {emp.employee_calling_name || "—"}
                    </td>
                    <td className="px-3 py-3">{emp.employee_gender || "—"}</td>
                    <td className="px-3 py-3">{emp.employee_email || "—"}</td>
                    <td className="px-3 py-3">{emp.employee_nic || "—"}</td>
                    <td className="px-3 py-3">{emp.employee_contact_no || "—"}</td>

                    <td className="px-3 py-3">{emp.department_name || "—"}</td>
                    <td className="px-3 py-3">{emp.designation_name || "—"}</td>

                    <td className="px-3 py-3">
                      <button
                        className="inline-flex items-center gap-1 px-2 py-1 border rounded-md text-blue-600 border-blue-600 hover:bg-blue-50"
                        onClick={() =>
                          navigate(
                            `/view-registered-members?employee_no=${encodeURIComponent(
                              emp.employee_no || ""
                            )}`
                          )
                        }
                        title="View"
                      >
                        <IoEyeOutline size={16} />
                        View
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          {/* Footer / pagination info */}
          <div className="flex justify-between items-center mt-4 text-sm text-gray-500 mx-1">
            <span>
              Showing{" "}
              {employeeData.length > 0
                ? (currentPage - 1) * ITEMS_PER_PAGE + 1
                : 0}{" "}
              to {(currentPage - 1) * ITEMS_PER_PAGE + employeeData.length} of{" "}
              {totalRecords} entries
            </span>

            <div className="flex gap-1 items-center">
              {/* Prev group */}
              {pageGroupStart > 1 && (
                <button
                  className="px-3 py-1 border rounded-md hover:bg-gray-100"
                  onClick={() => {
                    const newStart = Math.max(pageGroupStart - PAGE_GROUP_SIZE, 1);
                    setPageGroupStart(newStart);
                    setCurrentPage(newStart);
                  }}
                  disabled={isLoading}
                >
                  ← Prev
                </button>
              )}

              {/* Page numbers */}
              {visiblePageNumbers.map((n) => (
                <button
                  key={n}
                  className={`px-3 py-1 border rounded-md ${currentPage === n ? "bg-blue-600 text-white" : "hover:bg-gray-100"
                    }`}
                  onClick={() => setCurrentPage(n)}
                  disabled={isLoading}
                >
                  {n}
                </button>
              ))}

              {/* Next group */}
              {pageGroupStart + PAGE_GROUP_SIZE <= totalPages && (
                <button
                  className="px-3 py-1 border rounded-md hover:bg-gray-100"
                  onClick={() => {
                    const newStart = pageGroupStart + PAGE_GROUP_SIZE;
                    setPageGroupStart(newStart);
                    setCurrentPage(newStart);
                  }}
                  disabled={isLoading}
                >
                  Next →
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <ToastContainer position="top-right" autoClose={3000} hideProgressBar />
    </motion.div>
  );
}